// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
// See https://jira.tamg.io/browse/WP-2462
import * as React from 'react';
import { ModalBase, useBuggyWebkit, CloseIcon, type CloseIconColorVariant } from '@ta/modal.core';
import ContainerSheet from './ContainerSheet';
import ModalBody from './ModalBody';
import styles from './VanillaModal.css';

/**
 * A good ol' TripAdvisor modal. It's got a header and backdrop and is sometimes blurry, just the way we like it.
 */

type VerticalPlacement =
  | 'centered'
  | { top: string | number }
  | { bottom: string | number }
  | { top: string | number, bottom: string | number };

type Props = {|
  /** A callback fired to indicate the modal should close */
  onClose?: () => mixed,
  /** The header node to render; comes before the body */
  header?: React.Node,
  /** Where modal should be vertically aligned; defaults to center alignment */
  vertical?: VerticalPlacement,
  /** A fixed height of the modal */
  height?: string | number,
  /** A fixed width of the modal */
  width?: string | number,
  /** If true, then there is a close icon that will fire the onClose callback on click */
  enableCloseIcon?: boolean,
  /** The color of the close icon */
  closeIconColor?: CloseIconColorVariant,
  /** Props to pass through to the outer container */
  containerProps?: React.ElementConfig<typeof ContainerSheet>,
  /** Props to pass through to the body of the container */
  bodyProps?: React.ElementConfig<typeof ModalBody>,
  /** If true, focus will not automatically shift focus on open. For accessibility reasons, this value should be false */
  disableAutoFocus?: boolean,
  /** The color of the backdrop */
  backdropShade?: 'light' | 'dark',
  /** The contents of the modal */
  children: React.Node,
|};

const VanillaModal = ({
  onClose,
  header,
  vertical,
  height,
  width,
  enableCloseIcon = true,
  closeIconColor = 'TA_GREEN',
  containerProps,
  bodyProps,
  disableAutoFocus,
  backdropShade,
  children,
}: Props) => {
  const isBuggy = useBuggyWebkit();
  const style = {
    ...(vertical !== undefined && vertical !== 'centered'
      ? { ...vertical, transform: 'translate(-50%)' }
      : {}),
    ...(isBuggy ? { height: 'calc(100% - 4rem)' } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(width !== undefined ? { width } : {}),
  };

  return (
    <ModalBase
      style={style}
      className={styles.modal}
      onClose={onClose}
      disableAutoFocus={disableAutoFocus}
      backdropShade={backdropShade}
    >
      <ContainerSheet {...containerProps}>
        {header}
        <ModalBody {...bodyProps}>{children}</ModalBody>
        {enableCloseIcon && (
          <CloseIcon onClick={onClose} colorVariant={closeIconColor} onEnterKeyDown={onClose} />
        )}
      </ContainerSheet>
    </ModalBase>
  );
};

export default VanillaModal;
